import { darken, lighten, readableTextOn, withAlpha } from './colorMath.js'
import { activeProfile } from './profiles/index.js'

export const DEFAULT_BRAND_COLORS = activeProfile.brand

/**
 * Static design scales for the active profile: type, spacing, radii, shadows,
 * greys, semantic colours, surfaces and z-index. The values live in
 * theme/profiles, so re-skinning the app is a profile switch rather than an edit
 * spread across this file.
 */
export const scales = activeProfile.scales

/** Ten-step ramp around a single brand hex, matching the previous hand-tuned scale. */
const buildRamp = (base) => ({
  50: lighten(base, 0.92),
  100: lighten(base, 0.85),
  200: lighten(base, 0.7),
  300: lighten(base, 0.5),
  400: lighten(base, 0.25),
  500: base,
  600: darken(base, 0.12),
  700: darken(base, 0.22),
  800: darken(base, 0.32),
  900: darken(base, 0.42),
  950: darken(base, 0.52),
})

/**
 * Resolves the full token set for the current entreprise settings.
 * Everything downstream (CSS variables, the kit, the shim) reads from this one
 * function.
 *
 * @param {{couleur_primaire?: string, couleur_secondaire?: string, couleur_accent?: string}|null} settings
 */
export const buildTokens = (settings) => {
  const primary = settings?.couleur_primaire || DEFAULT_BRAND_COLORS.couleur_primaire
  const secondary =
    settings?.couleur_secondaire || DEFAULT_BRAND_COLORS.couleur_secondaire
  const accent = settings?.couleur_accent || DEFAULT_BRAND_COLORS.couleur_accent

  /**
   * Foreground picking uses the profile's own near-black rather than a fixed
   * grey, which is what lets saturated mid-tone brands still reach AA: on the
   * Nomad green, #1f2937 gives 4.23:1 while the profile ink gives 5.28:1.
   */
  const onFill = (hex) => readableTextOn(hex, scales.text.dark)

  /**
   * A profile may declare the ink for a brand fill instead of deriving it, which
   * is how a reference pairing gets reproduced even where it misses AA. Anything
   * not declared still goes through the contrast-driven pick above.
   */
  const declaredInk = activeProfile.brandInk ?? {}
  const inkFor = (key, hex) => declaredInk[key] ?? onFill(hex)

  return {
    ...scales,
    brand: {
      primary,
      primaryHover: darken(primary, 0.14),
      primaryActive: darken(primary, 0.22),
      primarySoft: withAlpha(primary, activeProfile.softAlpha),
      onPrimary: inkFor('onPrimary', primary),
      secondary,
      secondaryHover: darken(secondary, 0.12),
      secondarySoft: withAlpha(secondary, 0.1),
      onSecondary: inkFor('onSecondary', secondary),
      accent,
      accentHover: darken(accent, 0.12),
      onAccent: inkFor('onAccent', accent),
    },
    primaryRamp: buildRamp(primary),
    accentRamp: {
      300: lighten(accent, 0.35),
      400: lighten(accent, 0.18),
      500: accent,
      600: darken(accent, 0.12),
    },
    component: activeProfile.buildComponent({
      primary,
      secondary,
      accent,
      scales,
      withAlpha,
      darken,
      readableTextOn: onFill,
    }),
  }
}

export default buildTokens
